import { EntryMode } from '../../@types/types'
import { IMMOBILIENSCOUT_24_PORTAL_URL, VERMIETET_DE_PORTAL_URL } from '../../config'
import { isWebView, preserveSearchParams } from '../navigation'

const EXPOSE_MANAGER_URL_BASE = '/scoutmanager/exposemanager/'
const MY_SCOUT_URL_BASE = '/meinkonto/start/'
const CONTINGENT_OPA_REDIRECT_URL_BASE = '/scoutmanager/angebot/veroeffentlichenAusAngebotsManager.html'
const CLB_URL_BASE = `/anbieten/private-anbieter/inserieren/realEstate/offer-api/`
const SCOUT_MANAGER_OVERVIEW_URL_BASE = `/scoutmanager/angebotsliste/app/overview.html`
const VERMIETET_DE_URL_BASE = `/next/shelf-rental-overview/`
const HOMEOWNER_WORLD_URL_BASE = `/meinkonto/meine-immobilien`
const MESSAGE_MANAGER_URL_BASE = `/nachrichten-manager/`
const WAITING_LIST_REDIRECT_URL_BASE = `/inbox/conversations?modal_type=WAITING_LIST`
const SOURCE_EXIT_PROGRESS_STATES = new Set(['SENT_TO_OFFER_API'])

const CALLBACK_URL_KEY = 'callbackUrl'
const CANCEL_URL_KEY = 'cancelUrl'
const SHOW_PROGRESS_BAR_KEY = 'showProgressBar'
const HEADER_TYPE_KEY = 'cmsHeader'
const REAL_ESTATE_ID = 'realEstateId'

export function buildExitUrl(
  isContingentCustomer: boolean,
  realEstateId: number,
  entryMode: EntryMode,
  segmentation: string,
  transaction: string,
  category: string,
  buildingId?: string,
  unitId?: string,
  isWaitingListFlow?: boolean,
  published?: boolean,
  progressState?: string
): string {
  const exitUrl = function () {
    switch (entryMode) {
      case 'EDIT_FROM_OFFER_OVERVIEW':
        if (!shouldRedirectEditToOpa(published, progressState)) {
          return isWebView()
            ? 'is24://retargetShowOffer'
            : `${IMMOBILIENSCOUT_24_PORTAL_URL}${SCOUT_MANAGER_OVERVIEW_URL_BASE}`
        }
        return buildOpaExitUrl(realEstateId, entryMode, segmentation, transaction, category, buildingId, unitId)
      case 'EDIT_FROM_OXM':
        return isWebView()
          ? 'is24://retargetShowOffer'
          : `${IMMOBILIENSCOUT_24_PORTAL_URL}${EXPOSE_MANAGER_URL_BASE}${realEstateId}`
      case 'EDIT_FOR_ENERGY_CERTIFICATE':
        return `${IMMOBILIENSCOUT_24_PORTAL_URL}${EXPOSE_MANAGER_URL_BASE}${realEstateId}`
      case 'EDIT_FROM_MY_SCOUT':
        if (!shouldRedirectEditToOpa(published, progressState)) {
          return `${IMMOBILIENSCOUT_24_PORTAL_URL}${MY_SCOUT_URL_BASE}?listingId=${realEstateId}`
        }
        return buildOpaExitUrl(realEstateId, entryMode, segmentation, transaction, category, buildingId, unitId)
      case 'EDIT_FROM_VDE':
        return buildVermietetDeUrl(buildingId, unitId)
      case 'EDIT_FROM_HOMEOWNER_WORLD':
        return isWebView() ? `is24://myProperty` : `${IMMOBILIENSCOUT_24_PORTAL_URL}${HOMEOWNER_WORLD_URL_BASE}`
      case 'EDIT_FROM_PROPERTY_HUB_O4O':
        return `${IMMOBILIENSCOUT_24_PORTAL_URL}${HOMEOWNER_WORLD_URL_BASE}/immobilien/expose/${realEstateId}?navigate=o4OUpdateFlow`
      default:
        if (isWaitingListFlow) {
          return `${IMMOBILIENSCOUT_24_PORTAL_URL}${MESSAGE_MANAGER_URL_BASE}${realEstateId}${WAITING_LIST_REDIRECT_URL_BASE}`
        }
        if (isContingentCustomer) {
          return `${IMMOBILIENSCOUT_24_PORTAL_URL}${CONTINGENT_OPA_REDIRECT_URL_BASE}?realEstateId=${realEstateId}`
        }
        return buildOpaExitUrl(realEstateId, entryMode, segmentation, transaction, category, buildingId, unitId)
    }
  }
  return exitUrl()
}

function shouldRedirectEditToOpa(published?: boolean, progressState?: string): boolean {
  return published !== true && !SOURCE_EXIT_PROGRESS_STATES.has(progressState ?? '')
}

function buildOpaExitUrl(
  realEstateId: number,
  entryMode: EntryMode,
  segmentation: string,
  transaction: string,
  category: string,
  buildingId?: string,
  unitId?: string
): string {
  const searchParams = new URLSearchParams(window.location.search)
  const isWebViewSet = isWebView()
  const callBackUrl =
    entryMode === 'VDE_INSERTION'
      ? buildVermietetDeUrl(buildingId, unitId)
      : `${IMMOBILIENSCOUT_24_PORTAL_URL}${SCOUT_MANAGER_OVERVIEW_URL_BASE}`
  const cancelUrl = `${IMMOBILIENSCOUT_24_PORTAL_URL}${CLB_URL_BASE}${realEstateId}`

  searchParams.set(CALLBACK_URL_KEY, btoa(callBackUrl))
  searchParams.set(CANCEL_URL_KEY, btoa(preserveSearchParams(cancelUrl)))
  searchParams.set(SHOW_PROGRESS_BAR_KEY, 'false')
  searchParams.set(HEADER_TYPE_KEY, 'pip')

  if (isWebViewSet) {
    searchParams.set('customerSegment', segmentation.toLowerCase())
    searchParams.set('realEstateType', `${category}_${transaction}`.toLowerCase())
    searchParams.set(REAL_ESTATE_ID, realEstateId.toString())
    searchParams.set('pageTitle', 'ppa')
    searchParams.set('utmContent', 'insertion_flow')
    searchParams.set('campaign', 'insertion')
    return `is24://retargetPublicationFlow?${searchParams.toString()}`
  }

  const basePath = `${IMMOBILIENSCOUT_24_PORTAL_URL}/scoutmanager/veroeffentlichung/veroeffentlichen/${realEstateId}`
  return preserveSearchParams(`${basePath}?${searchParams.toString()}`)
}

function buildVermietetDeUrl(buildingId?: string, unitId?: string): string {
  function concatenateWithSlash(...values: (string | null | undefined)[]): string {
    return values.filter((v) => v).join('/')
  }

  return `${VERMIETET_DE_PORTAL_URL}${VERMIETET_DE_URL_BASE}${concatenateWithSlash(buildingId, unitId)}`
}
